import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BadgeCheck, Lock, MapPin, Play, ShieldCheck, Sparkles, Timer } from "lucide-react";
import { PLAY_STORE_URL, VARIANT, VIP_LOGIN_URL, VIP_REGISTER_URL } from "@/constants/links";
import { track } from "@/lib/tracker";

const HERO_IMG =
  "https://images.pexels.com/photos/5523110/pexels-photo-5523110.jpeg?auto=compress&cs=tinysrgb&w=1100&h=1320&fit=crop";
const HERO_SRC_SET = [
  "https://images.pexels.com/photos/5523110/pexels-photo-5523110.jpeg?auto=compress&cs=tinysrgb&w=480&h=576&fit=crop 480w",
  "https://images.pexels.com/photos/5523110/pexels-photo-5523110.jpeg?auto=compress&cs=tinysrgb&w=768&h=922&fit=crop 768w",
  `${HERO_IMG} 1100w`,
].join(", ");

/**
 * VIP: schermata REALE dell'app (acquisita da dispositivo in test, status bar
 * rimossa e nome profilo offuscato) al posto della foto stock. Il visitatore
 * vede l'interfaccia vera del prodotto.
 * Asset in public/app-screens/panoramica.png (540x1044), servito dal vivo sotto
 * /static/ perché il Caddyfile di produzione manda al file_server del volume
 * landing SOLO i prefissi /static/* e i path in whitelist esplicita: qualunque
 * altro path nuovo finirebbe sul proxy Next.js e darebbe 404.
 */
const HERO_APP_SHOT = "/static/app-screens/panoramica.png";

const Star = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12 2l2.4 6.9L21 11l-6.6 2.1L12 20l-2.4-6.9L3 11l6.6-2.1L12 2z" />
  </svg>
);

const Squiggle = () => (
  <svg
    viewBox="0 0 220 14"
    preserveAspectRatio="none"
    aria-hidden="true"
    className="absolute -bottom-2 left-0 h-3 w-full md:-bottom-3"
  >
    <path
      d="M3 10 C 40 2, 80 12, 120 7 S 190 3, 217 8"
      fill="none"
      stroke="#FFD335"
      strokeWidth="7"
      strokeLinecap="round"
    />
  </svg>
);

const lineReveal = {
  hidden: { y: "110%" },
  show: (delay) => ({
    y: 0,
    transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
  }),
};

const isVip = VARIANT === "vip";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yPhoto = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const yChip = useTransform(scrollYProgress, [0, 1], [0, 140]);

  const badge = isVip
    ? "Offerta avanzata dell’ecosistema Guardian Family"
    : "Parental control Android per famiglie";

  const chips = isVip
    ? [
        { icon: ShieldCheck, label: "Dashboard VIP dedicata", color: "text-violet" },
        { icon: Lock, label: "Installazione APK Android", color: "text-pinky-deep" },
        { icon: Timer, label: "Pagamento mensile con Stripe", color: "text-emerald-500" },
      ]
    : [
        { icon: BadgeCheck, label: "Supervisione trasparente", color: "text-violet" },
        { icon: Sparkles, label: "100% senza pubblicità", color: "text-pinky-deep" },
        { icon: Timer, label: "Associazione con codice", color: "text-emerald-500" },
      ];

  return (
    <section
      ref={ref}
      className="relative overflow-hidden pb-20 pt-32 md:pb-28 md:pt-44"
      style={{ background: "linear-gradient(180deg, #FFFBEB 0%, #FFFFFF 70%)" }}
    >
      <div aria-hidden="true" className="absolute -right-28 -top-24 h-96 w-96 rounded-full bg-yellow" />
      <div aria-hidden="true" className="absolute right-[30%] top-16 h-16 w-16 rounded-full bg-pinky" />
      <div aria-hidden="true" className="absolute -left-24 bottom-24 h-64 w-64 rounded-full bg-pinky/80" />
      <div aria-hidden="true" className="absolute left-[42%] bottom-8 h-20 w-20 rounded-full border-[12px] border-violet/60" />
      <div aria-hidden="true" className="absolute right-[8%] top-24 h-40 w-40 bg-dots-violet rounded-3xl" />
      <Star className="animate-float-slow absolute left-[6%] top-[38%] hidden h-9 w-9 text-violet lg:block" />
      <Star className="animate-float absolute right-[4%] bottom-24 hidden h-7 w-7 text-pinky-deep lg:block" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-6">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="inline-flex items-center gap-2 rounded-full bg-violet px-4 py-2 text-xs font-bold uppercase tracking-widest text-white shadow-lg shadow-violet/30"
          >
            <Sparkles size={14} />
            {badge}
          </motion.span>

          <h1 className="mt-6 font-display text-4xl font-black leading-[1.06] tracking-tight text-ink sm:text-6xl lg:text-7xl">
            {isVip ? (
              <>
                <span className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    variants={lineReveal}
                    initial="hidden"
                    animate="show"
                    custom={0.15}
                  >
                    Guardian Family VIP.
                  </motion.span>
                </span>
                <span className="block overflow-hidden pb-3">
                  <motion.span
                    className="block"
                    variants={lineReveal}
                    initial="hidden"
                    animate="show"
                    custom={0.3}
                  >
                    Supervisione avanzata per{" "}
                    <span className="relative inline-block">
                      <em className="not-italic text-violet">tutta la famiglia.</em>
                      <Squiggle />
                    </span>
                  </motion.span>
                </span>
              </>
            ) : (
              <>
                <span className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    variants={lineReveal}
                    initial="hidden"
                    animate="show"
                    custom={0.15}
                  >
                    Guardian Family.
                  </motion.span>
                </span>
                <span className="block overflow-hidden pb-3">
                  <motion.span
                    className="block"
                    variants={lineReveal}
                    initial="hidden"
                    animate="show"
                    custom={0.3}
                  >
                    Il parental control{" "}
                    <span className="relative inline-block">
                      <em className="not-italic text-violet">trasparente</em>
                      <Squiggle />
                    </span>{" "}
                    per la vostra famiglia.
                  </motion.span>
                </span>
              </>
            )}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-6 max-w-lg text-base font-medium leading-relaxed text-ink/70 sm:text-lg"
          >
            {isVip
              ? "La versione avanzata di Guardian Family, con dashboard dedicata e account separati: intercettazione messaggistica WhatsApp, Telegram ecc., posizione, media e controllo avanzato, tutto in un unico pannello."
              : "Tempo schermo, posizione e regole app con protezione trasparente. Disponibile su Google Play a 3,89 € al mese."}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.62 }}
            className="mt-9 flex flex-col gap-5 sm:flex-row sm:items-center"
          >
            {isVip ? (
              <>
                <a
                  data-testid="hero-login-btn"
                  href={VIP_LOGIN_URL}
                  onClick={() => track("login_click")}
                  className="btn-3d-yellow inline-flex items-center justify-center gap-3 rounded-full px-7 py-4 text-base font-extrabold"
                >
                  <ShieldCheck size={20} />
                  Accedi alla dashboard VIP
                </a>
                <a
                  data-testid="hero-register-btn"
                  href={VIP_REGISTER_URL}
                  onClick={() => track("register_click")}
                  className="btn-3d-violet inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-base font-extrabold"
                >
                  Crea account VIP
                </a>
              </>
            ) : (
              <>
                <a
                  data-testid="hero-playstore-btn"
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("play_click")}
                  className="btn-3d-yellow inline-flex items-center justify-center gap-3 rounded-full px-7 py-4 text-base font-extrabold"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ink text-yellow">
                    <Play size={14} className="fill-current" />
                  </span>
                  Scarica da Google Play
                </a>
                <a
                  data-testid="hero-vip-teaser-btn"
                  href="#confronta"
                  onClick={() => track("vip_teaser_click")}
                  className="btn-3d-violet inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-base font-extrabold"
                >
                  VIP è un’app diversa — confronta
                </a>
              </>
            )}
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            {chips.map(({ icon: Icon, label, color }) => (
              <li
                key={label}
                className="flex items-center gap-2 rounded-full border-2 border-ink/10 bg-white px-4 py-2 text-sm font-bold text-ink shadow-md shadow-ink/5"
              >
                <Icon size={16} className={color} />
                {label}
              </li>
            ))}
          </motion.ul>
        </div>

        <div className="lg:col-span-6">
          <motion.div
            style={{ y: yPhoto }}
            className={`relative mx-auto ${isVip ? "max-w-[380px]" : "max-w-[460px]"}`}
          >
            <div
              aria-hidden="true"
              className="absolute -right-10 -top-12 h-32 w-32 rounded-full bg-yellow"
            />
            <div
              aria-hidden="true"
              className="absolute -bottom-12 -left-12 h-36 w-36 rounded-full border-[14px] border-violet"
            />
            <div
              aria-hidden="true"
              className="absolute -left-8 top-1/3 h-24 w-24 rounded-full bg-pinky"
            />
            <div
              aria-hidden="true"
              className="absolute -bottom-8 right-10 h-14 w-14 bg-dots-violet rounded-2xl"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="gloss-frame relative rotate-2 overflow-hidden rounded-[3rem] border-8 border-white"
            >
              {isVip ? (
                <div className="overflow-hidden rounded-[2.2rem] bg-[#FBFAF7]">
                  <img
                    src={HERO_APP_SHOT}
                    alt="Schermata reale della dashboard genitore di Guardian Family VIP: profili figlio, dispositivi online con stato e batteria"
                    width="540"
                    height="1044"
                    data-testid="hero-app-screenshot"
                    className="block h-auto w-full"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                  />
                </div>
              ) : (
                <img
                  src={HERO_IMG}
                  srcSet={HERO_SRC_SET}
                  sizes="(min-width: 1024px) 460px, (min-width: 640px) 75vw, 100vw"
                  alt="Una mamma bionda e la sua bambina guardano insieme lo smartphone davanti alla finestra di casa"
                  width="1000"
                  height="1200"
                  className="aspect-[5/6] h-full w-full object-cover"
                  style={{ filter: "saturate(1.1) contrast(1.06)" }}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              )}
            </motion.div>

            <motion.div style={{ y: yChip }} className="absolute -left-4 bottom-10 sm:-left-12">
              <div className="animate-float rounded-3xl border-2 border-ink/10 bg-white p-4 shadow-[0_24px_44px_-16px_rgba(31,31,37,0.35)] backdrop-blur">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-violet text-white shadow-lg shadow-violet/40">
                    <Timer size={18} />
                  </span>
                  <div>
                    <p className="text-[11px] font-bold text-ink/60">Tempo schermo di oggi</p>
                    <p className="font-display text-xl font-black text-ink">2h 15m</p>
                  </div>
                </div>
                <div className="mt-3 h-2.5 w-36 overflow-hidden rounded-full bg-softpurple">
                  <div className="h-full w-[62%] rounded-full bg-violet" />
                </div>
              </div>
            </motion.div>

            <motion.div
              style={{ y: yChip }}
              className="absolute -right-3 -top-16 sm:-right-10 sm:top-auto sm:bottom-14"
            >
              <div className="animate-float-delayed rounded-3xl border-2 border-ink/10 bg-white p-4 shadow-[0_24px_44px_-16px_rgba(31,31,37,0.35)] backdrop-blur">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-pinky-deep text-white shadow-lg shadow-pinky-deep/40">
                    <MapPin size={18} />
                  </span>
                  <div>
                    <p className="text-[11px] font-bold text-ink/60">Luogo sicuro</p>
                    <p className="text-sm font-extrabold text-ink">Arrivo a scuola</p>
                  </div>
                  <span className="ml-2 flex items-center gap-1.5 rounded-full bg-emerald-500 px-2.5 py-1 text-[11px] font-extrabold text-white shadow-md shadow-emerald-500/40">
                    <span className="h-1.5 w-1.5 rounded-full bg-white" />
                    OK
                  </span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
